import { useCallback, useState, type ChangeEvent, type FormEvent } from 'react';
import { Field } from '../components/Field';
import { RenewalBadge } from '../components/RenewalBadge';
import { Spinner } from '../components/Spinner';
import { addVehicle, deleteVehicle, listVehicles, updateVehicle, type Vehicle } from '../lib/catalogue';
import { errorMessage } from '../lib/errors';
import { useLoaded } from '../lib/hooks';
import { formatIsoDate, todayIso } from '../lib/dates';
import {
  PARTS,
  validateVehicle,
  type PartInput,
  type PartKey,
  type VehicleErrors,
  type VehicleFields,
  type VehicleInput,
} from '../lib/validation';

const ACTION_LABELS = { replaced: 'Replaced', serviced: 'Serviced' } as const;

export function VehiclesSection({ clientId }: { clientId: string }) {
  const load = useCallback(() => listVehicles(clientId), [clientId]);
  const { loading, value: vehicles, error, reload } = useLoaded(clientId, load);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState('');

  async function remove(v: Vehicle) {
    if (!window.confirm(`Remove ${v.plate} from this client?`)) return;
    setActionError('');
    try {
      await deleteVehicle(clientId, v.id);
      reload();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  }

  return (
    <section className="vehicles">
      <div className="page-head">
        <h2>Vehicles</h2>
        {!adding && (
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            Add vehicle
          </button>
        )}
      </div>

      {(error || actionError) && (
        <p className="alert alert--error" role="alert">
          {error || actionError}
        </p>
      )}

      {adding && (
        <VehicleForm
          submitLabel="Add vehicle"
          onSubmit={async (fields) => {
            await addVehicle(clientId, fields);
            setAdding(false);
            reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {loading ? (
        <Spinner />
      ) : !vehicles || vehicles.length === 0 ? (
        !adding && <p className="empty">No vehicles yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Plate</th>
                <th scope="col">Make</th>
                <th scope="col">Model</th>
                <th scope="col">Licence renewal</th>
                <th scope="col">Parts</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) =>
                editingId === v.id ? (
                  <tr key={v.id}>
                    <td colSpan={6}>
                      <VehicleForm
                        initial={v}
                        submitLabel="Save vehicle"
                        onSubmit={async (fields) => {
                          await updateVehicle(clientId, v.id, fields, v.updatedAt);
                          setEditingId(null);
                          reload();
                        }}
                        onCancel={() => setEditingId(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={v.id}>
                    <td className="plate">{v.plate}</td>
                    <td>{v.make}</td>
                    <td>{v.model}</td>
                    <td>
                      <RenewalBadge date={v.licenseRenewalDate} />
                    </td>
                    <td>
                      <PartsList vehicle={v} />
                    </td>
                    <td className="row-actions">
                      <button type="button" className="btn btn--small" onClick={() => setEditingId(v.id)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn--small btn--danger" onClick={() => void remove(v)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The parts recorded for a vehicle, each with what was done and when. */
function PartsList({ vehicle }: { vehicle: Vehicle }) {
  const recorded = PARTS.filter((p) => vehicle.parts[p.key]);
  if (recorded.length === 0) return <span className="muted">None recorded</span>;
  return (
    <ul className="parts-list">
      {recorded.map(({ key, label }) => {
        const part = vehicle.parts[key]!;
        return (
          <li key={key}>
            {label}: {ACTION_LABELS[part.action].toLowerCase()}{' '}
            <time dateTime={part.date}>{formatIsoDate(part.date)}</time>
          </li>
        );
      })}
    </ul>
  );
}

interface VehicleFormProps {
  initial?: Vehicle;
  submitLabel: string;
  onSubmit: (fields: VehicleFields) => Promise<void>;
  onCancel: () => void;
}

function VehicleForm({ initial, submitLabel, onSubmit, onCancel }: VehicleFormProps) {
  const [values, setValues] = useState<VehicleInput>({
    plate: initial?.plate ?? '',
    make: initial?.make ?? '',
    model: initial?.model ?? '',
    licenseRenewalDate: initial?.licenseRenewalDate ?? '',
  });
  const [parts, setParts] = useState<Record<PartKey, PartInput>>(
    () =>
      Object.fromEntries(
        PARTS.map(({ key }) => [key, initial?.parts[key] ?? { action: '', date: '' }]),
      ) as Record<PartKey, PartInput>,
  );
  const [errors, setErrors] = useState<VehicleErrors>({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const idPrefix = initial ? `v-${initial.id}` : 'v-new';

  const set = (name: keyof VehicleInput) => (e: ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [name]: e.target.value }));

  const setPart = (key: PartKey, change: Partial<PartInput>) =>
    setParts((p) => ({ ...p, [key]: { ...p[key], ...change } }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    const result = validateVehicle({ ...values, parts });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await onSubmit(result.value);
    } catch (err) {
      setFormError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form className="card form-grid form-grid--vehicle" onSubmit={submit} noValidate>
      <Field
        id={`${idPrefix}-plate`}
        label="Licence plate"
        name="plate"
        autoComplete="off"
        value={values.plate}
        onChange={set('plate')}
        error={errors.plate}
      />
      <Field
        id={`${idPrefix}-make`}
        label="Make"
        name="make"
        autoComplete="off"
        value={values.make}
        onChange={set('make')}
        error={errors.make}
      />
      <Field
        id={`${idPrefix}-model`}
        label="Model"
        name="model"
        autoComplete="off"
        value={values.model}
        onChange={set('model')}
        error={errors.model}
      />
      <Field
        id={`${idPrefix}-date`}
        label="Licence renewal date"
        name="licenseRenewalDate"
        type="date"
        value={values.licenseRenewalDate}
        onChange={set('licenseRenewalDate')}
        error={errors.licenseRenewalDate}
      />

      <fieldset className="parts form-grid__full">
        <legend>Parts: last replaced or serviced</legend>
        {PARTS.map(({ key, label }) => {
          const error = errors.parts?.[key];
          const id = `${idPrefix}-part-${key}`;
          return (
            <div key={key} className={error ? 'parts__row field--error' : 'parts__row'}>
              <span className="parts__name" id={`${id}-name`}>
                {label}
              </span>
              <select
                aria-labelledby={`${id}-name`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
                value={parts[key].action}
                onChange={(e) => {
                  const action = e.target.value as PartInput['action'];
                  // Clearing the action clears the record.
                  setPart(key, action ? { action } : { action, date: '' });
                }}
              >
                <option value="">Not recorded</option>
                <option value="replaced">{ACTION_LABELS.replaced}</option>
                <option value="serviced">{ACTION_LABELS.serviced}</option>
              </select>
              <input
                type="date"
                aria-label={`${label}: date`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
                max={todayIso()}
                value={parts[key].date}
                onChange={(e) => setPart(key, { date: e.target.value })}
              />
              {error && (
                <small id={`${id}-error`} className="field__error">
                  {error}
                </small>
              )}
            </div>
          );
        })}
      </fieldset>

      {formError && (
        <p className="alert alert--error form-grid__full" role="alert">
          {formError}
        </p>
      )}
      <div className="actions form-grid__full">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
